
import './App.css'
import AWS from 'aws-sdk'
import store from './redux/store'
import { useEffect, useState } from 'react'
import * as utils from './lib/messageUtils'
import * as keys from './config/awsChimeConfig'
import { Channel } from './components/Chat/types'
import { MessagingConstants } from './lib/enums'
import messagingService from './services/MessagingService'
import Chat from './v2/components/Chat'
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material'
import MobileChat from './components/MobileChat'
import axiosInstances, { axiosInstance } from './config/axios'
import MinimizedComponent from './components/MinimizedChat'
import { useStyles } from './style/styles'
import { setJtToken } from './lib/cookieUtils'
import { logInfo, logError } from './lib/rumLogger'
import { getChannelArn } from './v2/lib/utils'
import { Sender } from './v2/components/Chat/types'

const AppV2 = () => {
  const classes = useStyles()
  const { axiosInstance } = axiosInstances
  const searchParams = window.location.search
  const tokenMatch = searchParams?.match(/(?:\?|&)token=([^&]+)/)
  const jtTokenMatch = searchParams?.match(/(?:\?|&)jtToken=([^&]+)/)
  const token = tokenMatch?.[1] || ""
  const jtToken = jtTokenMatch?.[1] || ""
  const [isLoading, setIsLoading] = useState<boolean>(false)

  /* Set jtToken to cookie for axios interceptor */
  useEffect(() => {
    if (jtToken) {
      setJtToken(jtToken)
    }
  }, [jtToken])

  const [userId, setUserId] = useState<string>("") // TODO: Set type (remove any)
  // const [channelId, setChannelId] = useState<string>("")
  const [externalMeetingId, setExternalMeetingId] = useState<string>("")
  const [user, setUser] = useState<Sender | null>(null)
  const [channel, setChannel] = useState<Channel>({ channelName: '', channelArn: null, metadata: null, isEndedConversation: false })

  // TODO: Set RUM DD

  /* Set Messaging Data */
  useEffect(() => {
    utils.decryptData(token).then(res => {
      console.log('v2.0: decrypted data', res)
      const { channelId, userId, externalMeetingId } = JSON.parse(res)

      if (userId) setUserId(userId)
      externalMeetingId ?? setExternalMeetingId(externalMeetingId)
      setMessagingData(channelId, userId)
      })
  }, [token])

  /* Initialize AWS Session and WebSocket connection after user is set */
  useEffect(() => {
    if(userId && userId !== '') {
      console.log('v2: Initializing AWS session for user:', userId)
      setAwsSession()
      /* STS expiry temp: 15 mins (buffer: 5 mins) */
      const tokenExpiryinMs = 10 * 60 * 1000
      const interval = setInterval(() => {
        setAwsSession();
      }, tokenExpiryinMs);
      return () => clearInterval(interval);
    }
  }, [userId])
  
  const setMessagingData = async (channelId: string, userId: string) => {
    setIsLoading(true)
    console.log("v2: setting messaging data", channelId, userId)
    fetchLoggedInUser(userId)
    await getChannel(channelId, userId, externalMeetingId)
    setIsLoading(false)
  }

  const getChannel = async (channelId: string, userId: string, externalMeetingId: string) => {
    if(channelId && userId) {
      const channelArn = getChannelArn(channelId)
      const channelDetails = await utils.getChannelMembershipDetails(channelArn, userId)
      console.log('v2: getchannelmembershipdetails', channelDetails)
      const channelDetailsNoMembership = await utils.getChannelDetails(channelArn, userId)
      console.log('v2: getchanneldetails without membership', channelDetailsNoMembership)
      // const returnedChannel = channelDetails?.ChannelMembership
      const returnedChannel = channelDetailsNoMembership?.Channel
      console.log('v2: returned CHANNEL', returnedChannel)
        if(returnedChannel) {
          const channelMetadata = JSON.parse(returnedChannel?.Metadata)
          // const adminUserArn = sender.role.toUpperCase() === MessagingConstants.RECRUITER ? sender.userArn : receiver.userArn
          const endConversationTimestamp = channelMetadata?.EndConversationTimestamp || null
          const isEndedConversation = endConversationTimestamp ? true : false
          setChannel( prevChannel => ({ ...prevChannel,
                                        channelArn: returnedChannel?.ChannelArn || null,
                                        channelName: returnedChannel?.Name || null,
                                        metadata: returnedChannel?.Metadata,
                                        isEndedConversation: isEndedConversation }))
          } else {
            setChannel( prevChannel => ({ ...prevChannel,
                                          channelArn: null,
                                          channelName: externalMeetingId,
                                          metadata: null,
                                          isEndedConversation: false }))
          }
        }
      else {
        setChannel( prevChannel => ({ ...prevChannel,
          channelArn: null,
          channelName: externalMeetingId,
          metadata: null,
          isEndedConversation: false }))        
        }
  }

  const fetchLoggedInUser = async (userId: string) => {
    // TODO: refactor to also cater when only external user id and role are available (no messaging user id)
    const fetchedUser = await utils.getUserInstanceArnv2(null, null, userId)
    const formattedUser: Sender = {
      id: fetchedUser?.MessagingUserId,
      email: fetchedUser?.Email,
      external_user_id: fetchedUser?.ExternalSystemUserId,
      first_name: fetchedUser?.FirstName,
      last_name: fetchedUser?.LastName,
      mobile: fetchedUser?.MobilePhoneNumbers,
      role: fetchedUser?.Role,
      termsAgreed: fetchedUser?.TermsOfServiceAccepted,
      timezone: fetchedUser?.Timezone,
      userArn: fetchedUser?.MessagingUserId,
    }
    setUser(formattedUser)
  }

  // TODO: Transfer to utils
  const setAwsSession = async () => {
    try {
      axiosInstance.get(`/cognito/get/token`).then(res => {
        if(res.status === 200) {
          const tempToken = res.data?.token
          utils.decryptData(tempToken).then(async res => {
            const creds = JSON.parse(res)
            AWS.config.region = keys.AWS_REGION
            AWS.config.update({
              accessKeyId: creds?.code,
              secretAccessKey: creds.user,
              sessionToken: creds.token
            })
            messagingService.connect(userId)
        })
        console.log('v2: connected')
        } else {
          logInfo('aws_session_retry', 'setAwsSession', 'AWS ERROR in aws keys: Retrying...', res.status)
          setTimeout(setAwsSession, 30 * 1000)
        }
      })
    } catch(error) {
      logError(error as Error, 'setAwsSession', { action: 'cognito_retry', message: 'Cognito error: Retrying...' })
      setTimeout(setAwsSession, 30 * 1000)
    }
  }

  return (
    // <Provider store={store}>
    <>
      {!isLoading ? (
        <div className="App">
          { user && (
            <Chat
              user={user}
              channel={channel}
            />
          )}
          </div>
          // TODO: Add mobile
      ) : (
        <div className="App">
          <Box className={classes.centeredSpinner}>
            <CircularProgress size={40} thickness={5} color="primary" />
          </Box>
        </div>
      )}
      </>
    // </Provider>
  )
}

export default AppV2
